import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/services/api';
import {
  buildJoanCodePayload,
  supplierApiMessage,
  type JoanCodeFormData,
  type JoanCodeType,
} from '@/lib/schemas/joan-code';

/**
 * Portes de l'hotel (les 12 plaques de JOAN) servides pel PMS: `plans/doors-payloads-from-pms.md`,
 * fitxa P3. Contracte = els endpoints de les fitxes P1.7 i P2, tots amb JWT i rol ADMIN.
 *
 * - `GET /joan/doors-status` · estat de cada porta (quan va preguntar, voltatge, codis de personal)
 * - `GET /joan/activity-log?door=&limit=` · els últims intents, concedits i denegats
 * - `GET /access-codes/joan` · codis de personal actius i revocats dels últims 30 dies
 * - `POST /access-codes/joan` · alta (409 amb missatge clar si hi ha col·lisió)
 * - `POST /access-codes/joan/revoke` · baixa per (codi, nom)
 *
 * El «mode prova» de la pantalla només canvia el `refetchInterval` (5 s en comptes de 60 s): al
 * servidor no hi toca res.
 */

// ═══════════════════════════════════════════════════════════════
// Tipus del contracte
// ═══════════════════════════════════════════════════════════════

export interface JoanDoorStatus {
  doorId: number | string;
  name: string;
  type: string;
  /** Últim cop que la placa va demanar els codis. Les plaques pregunten cada 5 minuts. */
  lastPolledAt: string | null;
  lastVoltage: number | string | null;
  /** Codis de personal actius en aquella porta. El backend pot dir-li d'alguna d'aquestes tres maneres. */
  staffCodes?: number | null;
  staffCodesCount?: number | null;
  activeStaffCodes?: number | null;
}

export interface JoanActivityItem {
  id?: string;
  doorName: string;
  uid: string;
  name: string;
  granted: boolean;
  createdAt: string;
}

/** Una porta dins d'un grup de codis: el backend pot donar el nom cru o un objecte. */
export type JoanCodeDoor =
  | string
  | { id?: string; lockId?: string; name?: string | null; lockName?: string | null; lockType?: string };

export interface JoanCodeGroup {
  code: string;
  name: string;
  type: JoanCodeType | string;
  status?: string | null;
  validFrom?: string | null;
  validUntil?: string | null;
  /** Franja diària `HH:mm` de Madrid (proveïdors); `null` = tot el dia. */
  dailyFrom?: string | null;
  dailyUntil?: string | null;
  revokedAt?: string | null;
  doors?: JoanCodeDoor[] | null;
  locks?: JoanCodeDoor[] | null;
}

// ═══════════════════════════════════════════════════════════════
// Normalitzadors (els noms exactes dels camps els quadra l'orquestrador)
// ═══════════════════════════════════════════════════════════════

/** Nombre de codis de personal d'una porta, vingui amb el nom que vingui. */
export function doorStaffCodes(door: JoanDoorStatus): number {
  return door.staffCodesCount ?? door.staffCodes ?? door.activeStaffCodes ?? 0;
}

/** Noms de les portes d'un grup de codis, tant si vénen com a text com si vénen com a objecte. */
export function codeDoorNames(group: JoanCodeGroup): string[] {
  const raw = group.doors ?? group.locks ?? [];
  return raw
    .map((door) => (typeof door === 'string' ? door : (door.name ?? door.lockName ?? '')))
    .filter((name): name is string => name.length > 0);
}

/** Un grup és viu mentre no l'hagin revocat. */
export function isCodeActive(group: JoanCodeGroup): boolean {
  if (group.revokedAt) return false;
  return (group.status ?? 'ACTIVE').toUpperCase() === 'ACTIVE';
}

/** Minuts des de l'última pregunta de la placa. `null` si no ha preguntat mai. */
export function minutesSincePoll(lastPolledAt: string | null | undefined, now: Date = new Date()): number | null {
  if (!lastPolledAt) return null;
  const polled = new Date(lastPolledAt).getTime();
  if (Number.isNaN(polled)) return null;
  return Math.max(0, Math.floor((now.getTime() - polled) / 60_000));
}

/** Les plaques pregunten cada 5 min: més de 12 sense dir res vol dir que alguna cosa va malament. */
export const DOOR_STALE_MINUTES = 12;

export function isDoorStale(lastPolledAt: string | null | undefined, now: Date = new Date()): boolean {
  const minutes = minutesSincePoll(lastPolledAt, now);
  return minutes === null || minutes > DOOR_STALE_MINUTES;
}

// ═══════════════════════════════════════════════════════════════
// Claus i intervals
// ═══════════════════════════════════════════════════════════════

export const joanDoorsKeys = {
  all: ['joan-doors'] as const,
  status: () => [...joanDoorsKeys.all, 'status'] as const,
  activity: (filters: { door?: string; limit?: number }) =>
    [...joanDoorsKeys.all, 'activity', filters] as const,
  codes: () => [...joanDoorsKeys.all, 'codes'] as const,
};

/** «Mode prova» encès: 5 s. Apagat: 60 s. */
export const DOORS_REFETCH_MS = 60_000;
export const DOORS_TEST_REFETCH_MS = 5_000;

export function doorsRefetchInterval(testMode: boolean): number {
  return testMode ? DOORS_TEST_REFETCH_MS : DOORS_REFETCH_MS;
}

export const ACTIVITY_LIMIT = 50;

// ═══════════════════════════════════════════════════════════════
// Consultes
// ═══════════════════════════════════════════════════════════════

export function useJoanDoorsStatus(testMode = false) {
  return useQuery<JoanDoorStatus[]>({
    queryKey: joanDoorsKeys.status(),
    queryFn: async () => {
      const res = await api.get('/joan/doors-status');
      return res.data;
    },
    refetchInterval: doorsRefetchInterval(testMode),
  });
}

export function useJoanActivityLog(door?: string, testMode = false, limit = ACTIVITY_LIMIT) {
  return useQuery<JoanActivityItem[]>({
    queryKey: joanDoorsKeys.activity({ door, limit }),
    queryFn: async () => {
      const res = await api.get('/joan/activity-log', { params: { door, limit } });
      return res.data;
    },
    refetchInterval: doorsRefetchInterval(testMode),
  });
}

/** Pany JOAN tal com el dona `/locks` (només el que necessita el formulari). */
export interface JoanLock {
  id: string;
  name: string;
  type: string;
  provider?: string | null;
  externalId?: string | null;
  /** JSON del proveïdor; `firmware` diu si és la placa nova (`esphome-v1`). Pot venir com a text. */
  providerData?: unknown;
}

/**
 * Panys JOAN per al desplegable «Triar» del formulari: `lockIds` del backend són ids de `Lock`
 * (cuid), i `doors-status` dona l'`externalId` de la placa, que no és el mateix. Clau pròpia per
 * no barrejar-se amb la de la pestanya de TTLock, que llegeix el mateix endpoint amb un altre tipus.
 */
export function useJoanLocks() {
  return useQuery<JoanLock[]>({
    queryKey: [...joanDoorsKeys.all, 'locks'] as const,
    queryFn: async () => {
      const res = await api.get('/locks');
      const locks: JoanLock[] = res.data ?? [];
      return locks.filter((lock) => lock.provider === 'JOAN');
    },
  });
}

export function useJoanCodes() {
  return useQuery<JoanCodeGroup[]>({
    queryKey: joanDoorsKeys.codes(),
    queryFn: async () => {
      const res = await api.get('/access-codes/joan');
      return res.data;
    },
  });
}

// ═══════════════════════════════════════════════════════════════
// Mutacions
// ═══════════════════════════════════════════════════════════════

/** Missatge d'un error d'API: el 409 de P2 ja porta el text bo per ensenyar-lo tal qual. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const response = (error as { response?: { data?: { message?: string | string[] } } })?.response;
  const message = response?.data?.message;
  if (Array.isArray(message)) return message.join('. ');
  if (typeof message === 'string' && message.length > 0) return message;
  return error instanceof Error && error.message ? error.message : fallback;
}

export function useCreateJoanCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: JoanCodeFormData) => {
      const res = await api.post('/access-codes/joan', buildJoanCodePayload(data));
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: joanDoorsKeys.codes() });
      queryClient.invalidateQueries({ queryKey: joanDoorsKeys.status() });
      toast.success(`Codi creat per a ${variables.name.trim()}`);
    },
    onError: (error: unknown) => {
      // El 400 de «cap entrada amb la placa nova» (proveïdors) es diu clar; la resta, tal qual.
      toast.error(supplierApiMessage(apiErrorMessage(error, 'No s’ha pogut crear el codi')));
    },
  });
}

export function useRevokeJoanCode() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { code: string; name: string }) => {
      const res = await api.post('/access-codes/joan/revoke', input);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: joanDoorsKeys.codes() });
      queryClient.invalidateQueries({ queryKey: joanDoorsKeys.status() });
      toast.success(`Codi de ${variables.name} revocat`);
    },
    onError: (error: unknown) => {
      toast.error(apiErrorMessage(error, 'No s’ha pogut revocar el codi'));
    },
  });
}
